/**
 * Authentication state management using TC39 Signals.
 */
import { Signal } from 'signal-polyfill';
import {
  getAccount,
  getUserEmail,
  getUserRole,
  loginRedirect,
  logoutRedirect,
  getAccessToken as getMsalAccessToken,
} from '../services/msal-auth.service';
import { environment } from '../environments/environment';

export type UserRole = 'Handler' | 'Secretary' | 'CourseDirector' | 'AppAdmin';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  email: string | null;
  role: UserRole | null;
  accessToken: string | null;
}

const defaultAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  email: null,
  role: null,
  accessToken: null,
};

const DEV_AUTH_STORAGE_KEY = 'dt-dev-auth-state';

interface StoredDevAuthState {
  email: string;
  role: UserRole;
  accessToken: string;
}

function isUserRole(value: unknown): value is UserRole {
  return (
    value === 'Handler' ||
    value === 'Secretary' ||
    value === 'CourseDirector' ||
    value === 'AppAdmin'
  );
}

function canUseDevAuthPersistence(): boolean {
  return !environment.production;
}

function writeStoredDevAuthState(state: StoredDevAuthState | null): void {
  if (!canUseDevAuthPersistence()) {
    return;
  }

  try {
    if (state) {
      sessionStorage.setItem(DEV_AUTH_STORAGE_KEY, JSON.stringify(state));
      return;
    }

    sessionStorage.removeItem(DEV_AUTH_STORAGE_KEY);
  } catch {
    // Ignore storage access failures in non-browser or restricted contexts.
  }
}

function readStoredDevAuthState(): AuthState | null {
  if (!canUseDevAuthPersistence()) {
    return null;
  }

  try {
    const rawValue = sessionStorage.getItem(DEV_AUTH_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<StoredDevAuthState>;
    if (
      typeof parsed.email !== 'string' ||
      typeof parsed.accessToken !== 'string' ||
      !isUserRole(parsed.role)
    ) {
      sessionStorage.removeItem(DEV_AUTH_STORAGE_KEY);
      return null;
    }

    return {
      isAuthenticated: true,
      isLoading: false,
      email: parsed.email,
      role: parsed.role,
      accessToken: parsed.accessToken,
    };
  } catch {
    sessionStorage.removeItem(DEV_AUTH_STORAGE_KEY);
    return null;
  }
}

function createInitialAuthState(): AuthState {
  return readStoredDevAuthState() ?? defaultAuthState;
}

/**
 * Reactive auth state signal.
 */
export const authState = new Signal.State<AuthState>(createInitialAuthState());

/**
 * In-flight silent token acquisition shared by concurrent callers.
 * Without this, every parallel API request starts its own MSAL acquisition
 * and each one writes authState when it resolves (#118).
 */
let accessTokenRequest: Promise<string | null> | null = null;

/**
 * Computed signal: is user authenticated and not loading
 */
export const isReady = new Signal.Computed(() => {
  const state = authState.get();
  return !state.isLoading;
});

/**
 * Computed signal: does user have secretary role
 */
export const isSecretary = new Signal.Computed(() => {
  const state = authState.get();
  return state.role === 'Secretary';
});

/**
 * Computed signal: does user have course director role
 */
export const isCourseDirector = new Signal.Computed(() => {
  const state = authState.get();
  return state.role === 'CourseDirector';
});

/**
 * Computed signal: can user access secretary features
 */
export const canAccessSecretaryFeatures = new Signal.Computed(() => {
  const state = authState.get();
  return (
    state.role === 'Secretary' || state.role === 'CourseDirector' || state.role === 'AppAdmin'
  );
});

/**
 * Computed signal: can user access the shared trial-event management UI
 */
export const canManageTrialEvents = new Signal.Computed(() => {
  const state = authState.get();
  return state.role === 'Secretary' || state.role === 'AppAdmin';
});

/**
 * Computed signal: can user access AppAdmin-only configuration features
 */
export const canAccessAppAdminConfiguration = new Signal.Computed(() => {
  const state = authState.get();
  return state.role === 'AppAdmin';
});

/**
 * Auth state management utilities
 */
export const authActions = {
  /**
   * Set the authentication state after login.
   */
  setAuthenticated(email: string, role: UserRole, accessToken: string): void {
    const nextState: AuthState = {
      isAuthenticated: true,
      isLoading: false,
      email,
      role,
      accessToken,
    };

    authState.set(nextState);
    writeStoredDevAuthState({ email, role, accessToken });
  },

  /**
   * Set loading state during auth operations.
   */
  setLoading(isLoading: boolean): void {
    const current = authState.get();
    authState.set({ ...current, isLoading });
  },

  /**
   * Clear authentication state on logout.
   */
  async logout(): Promise<void> {
    authState.set(defaultAuthState);
    writeStoredDevAuthState(null);
    await logoutRedirect();
  },

  /**
   * Initiate login flow using MSAL redirect.
   * In production, this redirects to Entra External ID.
   */
  async login(loginHint?: string): Promise<void> {
    authActions.setLoading(true);
    await loginRedirect(loginHint);
  },

  /**
   * Initiate login/signup with external identity provider.
   * All providers are handled by Entra External ID.
   */
  async loginWithProvider(_provider: string, _mode: 'login' | 'signup'): Promise<void> {
    const current = authState.get();
    authState.set({ ...current, isLoading: true });
    // MSAL handles all provider flows through Entra
    await loginRedirect();
  },

  /**
   * Initiate login/signup with email.
   * Handled by Entra External ID (supports email+password or OTP).
   */
  async loginWithEmail(email: string, _mode: 'login' | 'signup'): Promise<void> {
    const current = authState.get();
    authState.set({ ...current, isLoading: true });
    // Use loginHint to pre-fill email in Entra login form
    await loginRedirect(email);
  },

  /**
   * Restore authentication state from MSAL cache.
   * Call this during app initialization to restore user session.
   */
  async restoreAuthState(): Promise<void> {
    const account = await getAccount();
    if (!account) {
      authState.set(readStoredDevAuthState() ?? defaultAuthState);
      return;
    }

    const email = getUserEmail(account);
    const role = getUserRole(account);

    if (email && isUserRole(role)) {
      authState.set({
        isAuthenticated: true,
        isLoading: false,
        email,
        role,
        accessToken: null, // Will be acquired on demand
      });
      writeStoredDevAuthState(null);
    } else {
      authState.set(defaultAuthState);
      writeStoredDevAuthState(null);
    }
  },

  /**
   * Get access token for API calls.
   * Acquires token silently from cache or refreshes if needed.
   */
  async getAccessToken(): Promise<string | null> {
    const current = authState.get();
    if (current.isAuthenticated && current.accessToken) {
      return current.accessToken;
    }

    // Promise.resolve(...) so a non-promise return (e.g. a bare mock) still works.
    accessTokenRequest ??= Promise.resolve(getMsalAccessToken()).finally(() => {
      accessTokenRequest = null;
    });

    const token = await accessTokenRequest;
    if (token) {
      // Re-read state after the await: restoreAuthState() (or a login) may have
      // completed while MSAL was acquiring. Writing back the pre-await snapshot
      // would clobber those fields (e.g. reset isAuthenticated to false), and
      // every authState change re-renders the app shell — a stale write loop
      // here recreates the active page until the renderer dies (#118).
      const latest = authState.get();
      if (latest.accessToken !== token) {
        authState.set({ ...latest, accessToken: token });
      }
    }
    return token;
  },
};
